import {
  buildCalendarTaskFilterDocument,
  calendarFilterOptions,
  getCalendarTaskWindow,
} from '@griply/utils';
import { addDays } from 'date-fns';
import { useTasks, useTasksByFilterDocument } from 'features/tasks';
import { useAllTasksFilterDocument } from 'features/user';
import { useMemo } from 'react';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

export const useCalendarTasks = (
  startDate: Timestamp,
  dayRange: number,
  bufferWeeks = 2,
): Task[] => {
  const baseFilterDocument = useAllTasksFilterDocument();
  const { start, end } = getCalendarTaskWindow(
    startDate,
    addDays(startDate, dayRange - 1),
    bufferWeeks,
  );
  const startMs = start.getTime();
  const endMs = end.getTime();
  const filterDocument = useMemo(
    () =>
      buildCalendarTaskFilterDocument(baseFilterDocument, {
        start: new Date(startMs),
        end: new Date(endMs),
      }),
    [baseFilterDocument, startMs, endMs],
  );

  const windowedUncompleted = useTasksByFilterDocument(
    filterDocument,
    calendarFilterOptions,
  );
  const completed = useTasks({ completedOnly: true });

  return useMemo(() => {
    const byId = new Map<ID, Task>();
    [...windowedUncompleted, ...completed].forEach((task) =>
      byId.set(task.id, task),
    );
    return Array.from(byId.values());
  }, [windowedUncompleted, completed]);
};
